import { z } from 'zod';
import {
  DEFAULT_POLL_DURATION_DAYS,
  DEFAULT_POLL_VISIBILITY,
  POLL_MAX_OPTIONS,
  POLL_MIN_OPTIONS,
  POLL_OPTION_MAX_LEN,
  POLL_VISIBILITIES,
  isValidPollDuration,
  type PollDurationDays,
  type PollVisibility,
} from '@leanin/shared/polls';

/** A poll being authored in the composer: option labels (the server mints the
 *  ids on create), the chosen run length, and who can see votes. Mirrors the
 *  server `PollInput` shape so `handleSubmit` can forward it verbatim. */
export type PollDraft = {
  options: string[];
  durationDays: PollDurationDays;
  visibility: PollVisibility;
};

export function newPollDraft(): PollDraft {
  return {
    options: ['', '', ''],
    durationDays: DEFAULT_POLL_DURATION_DAYS,
    visibility: DEFAULT_POLL_VISIBILITY,
  };
}

// Settings fall back to defaults rather than dropping the member's typed
// options: drafts saved before `visibility` existed omit it, and a duration
// removed from the allowed set should not discard the whole poll.
const storedPollDraft = z.object({
  options: z.array(z.string().max(POLL_OPTION_MAX_LEN)).min(POLL_MIN_OPTIONS).max(POLL_MAX_OPTIONS),
  durationDays: z.number().refine(isValidPollDuration).catch(DEFAULT_POLL_DURATION_DAYS),
  visibility: z.enum(POLL_VISIBILITIES).catch(DEFAULT_POLL_VISIBILITY),
});

/** Parse a poll read back from the local draft store (untrusted: written by
 *  older builds, or edited by hand). Returns null when there is no poll or the
 *  stored options can't be recovered. */
export function parseStoredPollDraft(value: unknown): PollDraft | null {
  if (value == null) return null;
  const result = storedPollDraft.safeParse(value);
  return result.success ? result.data : null;
}
